import { type Color, FormDecoration, Modal } from "@general-backoffice/core/index";
import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Row } from "reactstrap";

interface ModalInfoFormContainerProps {
  info?: React.ReactNode;
  infoTitle?: React.ReactNode;
  infoColor?: Color;
  children?: React.ReactNode;
  size?: "sm" | "md" | "lg";
  hideHeader?: boolean;
  fullscreenMobile?: boolean;
  isOpen?: boolean;
  title?: string;
  buttonSave?: Record<string, unknown>;
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  toggleOpen?: () => void;
  closeText?: string;
  isLoading?: boolean;
  [key: string]: unknown;
}

const defaultInfoColor: Color = "light";
const ModalFormContainer = ({
  info,
  infoTitle,
  infoColor = defaultInfoColor,
  children,
  size,
  hideHeader,
  fullscreenMobile,
  ...modalProps
}: ModalInfoFormContainerProps) => {
  const [infoModalIsOpen, setInfoModalOpen] = useState(false);
  // The header/fullscreen overrides used to be global selectors, so while this
  // modal was mounted any other open modal lost its header and got a fixed
  // footer. Scope them to this instance instead.
  const scopeClass = useRef(`modal-scope-${Math.random().toString(36).slice(2, 10)}`).current;
  const scopeAnchorRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const modalEl = scopeAnchorRef.current?.closest(".modal");
    if (!modalEl) return;
    modalEl.classList.add(scopeClass);
    return () => modalEl.classList.remove(scopeClass);
  }, [scopeClass, modalProps?.isOpen, infoModalIsOpen]);

  return (
    <>
      <Modal.Simple
        title={typeof infoTitle === "string" ? infoTitle : ""}
        size="lg"
        isOpen={!!(modalProps?.isOpen && infoModalIsOpen)}
        toggleOpen={() => setInfoModalOpen(!infoModalIsOpen)}
        color={infoColor}
      >
        {info}
      </Modal.Simple>

      {/* @ts-expect-error - ModalFormContainer requires buttonSave/onSubmit but this wrapper provides info-only modals */}
      <Modal.FormContainer
        size={info ? "lg" : size || "lg"}
        {...modalProps}
        isOpen={!!(modalProps?.isOpen && !infoModalIsOpen)}
        title={hideHeader ? undefined : modalProps.title}
      >
        {/* The vendor modal exposes no className hook, so tag the rendered
            `.modal` ancestor from inside. The style block below is scoped to
            that class; it used to use bare `.modal-*` selectors, which hit any
            other modal open at the same time. */}
        <span ref={scopeAnchorRef} style={{ display: "none" }} aria-hidden="true" />
        {(hideHeader || fullscreenMobile) && (
          <style>{`
          ${hideHeader ? `.${scopeClass} .modal-header { display: none !important; }` : ""}
          ${
            fullscreenMobile
              ? `
            @media (max-width: 576px) {
              .${scopeClass}.modal { padding: 0 !important; }
              .${scopeClass} .modal-dialog {
                margin: 0 !important;
                max-width: 100% !important;
                width: 100% !important;
                height: 100dvh !important;
                max-height: 100dvh !important;
              }
              .${scopeClass} .modal-content {
                height: 100dvh !important;
                max-height: 100dvh !important;
                border-radius: 0 !important;
                border: none !important;
                overflow: hidden !important;
              }
              .${scopeClass} .modal-body {
                height: calc(100dvh - 70px - env(safe-area-inset-bottom)) !important;
                max-height: calc(100dvh - 70px - env(safe-area-inset-bottom)) !important;
                overflow-y: auto !important;
              }
              .${scopeClass} .modal-footer {
                position: fixed !important;
                bottom: 0 !important;
                left: 0 !important;
                right: 0 !important;
                background: white !important;
                border-top: 1px solid #dee2e6 !important;
                padding-bottom: calc(14px + env(safe-area-inset-bottom)) !important;
                z-index: 1050 !important;
              }
            }
          `
              : ""
          }
        `}</style>
        )}
        <Row className="m-0 position-relative mx--4">
          {info && (
            <>
              <Col
                className={
                  "my--4 p-4 position-sticky top--4 d-none d-lg-block text-break " +
                  (infoColor ? `bg-gradient-${infoColor}` : "")
                }
                style={{ overflowY: "auto", overflowX: "hidden", maxHeight: "60vh" }}
              >
                <div className="text-uppercase mb-4 font-weight-bold">{infoTitle}</div>
                {info}
              </Col>
              <Col xs="auto" className="p-0 d-none d-lg-block">
                <FormDecoration.Line vertical />
              </Col>

              <Button
                type="button"
                color={infoColor}
                className="w-100 mx-3 mt--3 mb-4 d-lg-none"
                onClick={() => setInfoModalOpen(true)}
              >
                {infoTitle}
              </Button>
            </>
          )}
          <Col className="px-4">{children}</Col>
        </Row>
      </Modal.FormContainer>
    </>
  );
};

export default ModalFormContainer;
